// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// Domain glossary. Terms are surfaced as clickable ℹ popovers via [data-term].

export interface GlossaryTerm { term: string; definition: string }

/** Inline glossary link markup — clicking it shows the definition popover. */
export function glossLink(key: keyof typeof GLOSSARY | string, label: string): string {
  return `<span class="gloss-link" data-term="${key}" role="button" tabindex="0" aria-label="Definition of ${label}">${label}<span class="gloss-i" aria-hidden="true">ⓘ</span></span>`;
}

export const GLOSSARY: Record<string, GlossaryTerm> = {
  tfr: {
    term: 'Total fertility rate (TFR)',
    definition: 'The average number of babies a woman would have over her lifetime if she experienced, at each age, the birth rates of the current year. It sums the age-specific fertility rates. A TFR of about 2.1 keeps a population stable without migration.',
  },
  replacement: {
    term: 'Replacement rate',
    definition: 'The fertility rate at which a generation exactly replaces itself — about 2.1 babies per woman in a developed country (slightly above 2 to allow for children who do not survive to adulthood). Below it, the population would shrink over time without migration.',
  },
  asfr: {
    term: 'Age-specific fertility rate',
    definition: 'The number of births to mothers of a given age group per 1,000 women of that age. Plotting it by age shows at what ages women are having children — the peak has shifted from the early 20s to the early 30s.',
  },
  cbr: {
    term: 'Crude birth rate',
    definition: 'Births per 1,000 people in the whole population in a year. It is "crude" because it ignores the age and sex structure of the population, so it is less comparable across places than the fertility rate.',
  },
  nuptial: {
    term: 'Nuptial birth',
    definition: 'A birth to parents who were married to each other at the time. "Ex-nuptial" births are to parents not married to each other — most are still registered by both parents.',
  },
  exnuptial: {
    term: 'Ex-nuptial birth',
    definition: 'A birth to parents who were not married to each other at the time of birth. It says nothing about whether the parents live together — many ex-nuptial births are to committed de facto couples and are registered by both parents.',
  },
  confinement: {
    term: 'Confinement',
    definition: 'A pregnancy that results in one or more births — a woman who has twins has one confinement but two births. The ABS reports median parental ages per confinement.',
  },
  medianAge: {
    term: 'Median age of mother',
    definition: 'The age at which half of mothers are younger and half older. It has risen from about 26 in the 1970s to over 31 today, reflecting people starting families later.',
  },
  registration: {
    term: 'Year of registration',
    definition: 'Births are counted by the year they were registered with the state or territory, which can be later than the year the baby was actually born. The seasonality view instead uses year and month of occurrence.',
  },
  occurrence: {
    term: 'Month of occurrence',
    definition: 'The actual calendar month a baby was born, used for the seasonality view. Because recent births take time to be registered, the latest year is incomplete and is excluded from that view.',
  },
  usualResidence: {
    term: 'State of usual residence',
    definition: 'Births are attributed to where the mother usually lives, not where the birth physically happened — so a baby delivered in a big-city hospital counts for the family’s home region.',
  },
};
